import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { BaseSafeFormView, BaseView, BaseText } from '@/components/base'
import { AppButton } from '@/components/AppButton'
import { StickyHeaderNavigation } from '@/components'
import { useTheme } from '@/hooks/useTheme'
import { useUserStore } from '@/store'
import SafeAreaLayout from '@/components/layout/SafeAreaLayout'
import { toast } from 'react-hot-toast'
import { ResendCountdown } from '@/components/ResendCountdown'
import { useResendVerificationEmail } from '@/domain'

export const CheckYourEmailScreen: React.FC = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()
  const email = useUserStore((state) => state.userAuthenticated?.email)

  const { mutateAsync: resendVerificationEmail } = useResendVerificationEmail()

  const handleError = () => {
    toast.error(t('api:errors.somethingWrong'))
  }

  const handleResendEmail = async () => {
    if (!email) {
      handleError()
      return
    }

    try {
      await resendVerificationEmail({ email })
      toast.success(t('common:resendVerificationLink'))
    } catch (error) {
      handleError()
    }
  }

  // Redirect if user already has phone number

  return (
    <>
      <BaseView className="h-screen flex flex-col overflow-hidden">
        <StickyHeaderNavigation showBackground={false} />
        <SafeAreaLayout isStretch hasNavBar>
          {/* Gradient background */}

          <BaseView
            className="fixed inset-0 z-0"
            style={{
              backgroundImage: 'url(/assets/images/email-bg.png)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
            }}
          />

          {/* Main content - copy of RN BaseSafeAreaView */}
          <BaseView
            flex={1}
            className="relative z-10 flex flex-col overflow-hidden"
            px={12}
            style={{
              paddingTop: 'env(safe-area-inset-top)', // Safe area support for iOS
              paddingLeft: 'env(safe-area-inset-left)',
              paddingRight: 'env(safe-area-inset-right)',
              minHeight: 0, // Important for flex containers
            }}
          >
            <BaseView
              p={20}
              align="center"
              justify="flex-end"
              gap={42}
              flex={1}
              style={{ overflow: 'hidden' }}
            >
              <div
                style={{
                  width: '100%',
                  maxHeight: '497px',
                  height: '100%',
                  marginTop: 'auto',
                  marginBottom: 'auto',
                  backgroundImage: 'url(/assets/images/big-email.png)',
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                }}
              />
              <BaseView gap={16} align="center">
                <BaseText
                  fontSize={28}
                  fontFamily="semibold"
                  color="label.default"
                  textAlign="center"
                >
                  {t('screens:checkEmail.verifyYourEmail')}
                </BaseText>

                <BaseText
                  fontSize={16}
                  fontFamily="regular"
                  color="label.default"
                  textAlign="center"
                >
                  {t('screens:checkEmail.sendEmailVerificationLink')}
                </BaseText>
              </BaseView>

              <ResendCountdown
                onPress={handleResendEmail}
                label={t('form:actions.sendLink')}
                countDownOnLabel={t('common:linkSent')}
                appButton={true}
                appButtonType="blur"
                style={{ backgroundColor: 'rgb(255 255 255 / 10%)' }}
                displayCountdown={true}
              />
            </BaseView>
          </BaseView>
        </SafeAreaLayout>
      </BaseView>
    </>
  )
}

export default CheckYourEmailScreen
